import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AudioState } from '../../types'
import { API_BASE_URL, getAuthHeader } from '../../lib/api'

interface AudioPlayerProps {
  audioState: AudioState
  onAudioStateChange: (state: AudioState) => void
}

const MEDIA_ARTWORK_SIZES = [96, 192, 512]

function formatTime(seconds: number) {
  const safeSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0)
  const mins = Math.floor(safeSeconds / 60)
  const secs = Math.floor(safeSeconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function toAbsoluteUrl(url: string) {
  if (!url) return url
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`
  }
  return url
}

function supportsDirectPlayback(url: string) {
  try {
    const resolved = new URL(url, window.location.origin)
    return resolved.searchParams.has('token') || resolved.protocol === 'blob:' || resolved.protocol === 'data:'
  } catch {
    return false
  }
}

function buildMediaArtwork(artworkUrl?: string) {
  if (!artworkUrl) return undefined
  const resolvedArtwork = toAbsoluteUrl(artworkUrl)
  return MEDIA_ARTWORK_SIZES.map((size) => ({
    src: resolvedArtwork,
    sizes: `${size}x${size}`,
  }))
}

export default function AudioPlayer({ audioState, onAudioStateChange }: AudioPlayerProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDocked, setIsDocked] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const resolvedSourceRef = useRef<string | null>(null)
  const audioStateRef = useRef(audioState)

  useEffect(() => {
    audioStateRef.current = audioState
  }, [audioState])

  useEffect(() => {
    setIsDocked(false)
  }, [audioState.topic?.id])

  const emitAudioState = useCallback(
    (patch: Partial<AudioState>) => {
      onAudioStateChange({
        ...audioStateRef.current,
        ...patch,
      })
    },
    [onAudioStateChange]
  )

  const cleanupBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
  }, [])

  const ensureAudioSource = useCallback(async () => {
    const audio = audioRef.current
    const sourceUrl = audioStateRef.current.topic?.audio_url
    if (!audio || !sourceUrl) return false

    const resolvedSource = toAbsoluteUrl(sourceUrl)
    if (resolvedSourceRef.current === resolvedSource && audio.src) {
      return true
    }

    cleanupBlobUrl()
    resolvedSourceRef.current = resolvedSource

    if (supportsDirectPlayback(resolvedSource)) {
      audio.src = resolvedSource
      audio.load()
      return true
    }

    try {
      const response = await fetch(resolvedSource, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
          Accept: 'audio/wav, audio/mpeg, audio/*',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Failed to load audio: ${response.status} ${response.statusText}`)
      }

      const blobUrl = URL.createObjectURL(await response.blob())
      blobUrlRef.current = blobUrl
      audio.src = blobUrl
      audio.load()
      return true
    } catch (error) {
      console.error('Failed to load audio:', error)
      emitAudioState({
        isPlaying: false,
        error: error instanceof Error ? error.message : 'Failed to load audio',
      })
      return false
    }
  }, [cleanupBlobUrl, emitAudioState])

  const playAudio = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    const loaded = await ensureAudioSource()
    if (!loaded) return

    try {
      await audio.play()
      emitAudioState({
        isPlaying: true,
        status: 'ready',
        error: undefined,
      })
    } catch (error) {
      console.error('Audio play error:', error)
      emitAudioState({
        isPlaying: false,
        error: error instanceof Error ? error.message : 'Unable to start playback',
      })
    }
  }, [emitAudioState, ensureAudioSource])

  const pauseAudio = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    emitAudioState({ isPlaying: false })
  }, [emitAudioState])

  const handlePlayPause = useCallback(async () => {
    if (audioStateRef.current.isPlaying) {
      pauseAudio()
      return
    }
    await playAudio()
  }, [pauseAudio, playAudio])

  const handleSeek = useCallback(
    (newTime: number) => {
      const audio = audioRef.current
      const clampedTime = Math.max(
        0,
        Math.min(audio?.duration || audioStateRef.current.duration || 0, newTime)
      )

      if (audio) {
        try {
          audio.currentTime = clampedTime
        } catch (error) {
          console.error('Seek error:', error)
        }
      }

      emitAudioState({ currentTime: clampedTime })
    },
    [emitAudioState]
  )

  const handleSkip = useCallback(
    (seconds: number) => {
      const audio = audioRef.current
      const currentTime = audio?.currentTime ?? audioStateRef.current.currentTime
      handleSeek(currentTime + seconds)
    },
    [handleSeek]
  )

  const handleClose = useCallback(() => {
    audioRef.current?.pause()
    cleanupBlobUrl()
    resolvedSourceRef.current = null
    if (audioRef.current) {
      audioRef.current.removeAttribute('src')
      audioRef.current.load()
    }
    onAudioStateChange({
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      status: 'idle',
    })
  }, [cleanupBlobUrl, onAudioStateChange])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTimeUpdate = () => {
      emitAudioState({
        currentTime: Math.floor(audio.currentTime || 0),
        duration: Number.isFinite(audio.duration) ? Math.floor(audio.duration) : audioStateRef.current.duration,
        isPlaying: !audio.paused,
      })
    }

    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) {
        emitAudioState({
          duration: Math.floor(audio.duration),
          status: 'ready',
        })
      }
    }

    const onPlay = () => emitAudioState({ isPlaying: true, status: 'ready' })
    const onPause = () => emitAudioState({ isPlaying: false })
    const onEnded = () => emitAudioState({ isPlaying: false, currentTime: 0 })
    const onError = () => emitAudioState({ isPlaying: false, error: 'Playback failed' })

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [emitAudioState])

  useEffect(() => {
    if (!audioState.topic?.audio_url) {
      cleanupBlobUrl()
      resolvedSourceRef.current = null
      return
    }

    let cancelled = false

    const syncSource = async () => {
      const loaded = await ensureAudioSource()
      if (!loaded || cancelled) return

      const audio = audioRef.current
      if (!audio) return

      if (audioState.isPlaying) {
        await playAudio()
      }
    }

    void syncSource()

    return () => {
      cancelled = true
    }
  }, [audioState.topic?.audio_url, audioState.isPlaying, cleanupBlobUrl, ensureAudioSource, playAudio])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioState.topic?.audio_url) return

    if (!audioState.isPlaying && !audio.paused) {
      audio.pause()
      return
    }

    if (audioState.isPlaying && audio.paused && audio.src) {
      void audio.play().catch((error) => {
        console.error('Audio resume error:', error)
        emitAudioState({
          isPlaying: false,
          error: error instanceof Error ? error.message : 'Unable to resume playback',
        })
      })
    }
  }, [audioState.isPlaying, audioState.topic?.audio_url, emitAudioState])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioState.topic?.audio_url || !Number.isFinite(audioState.currentTime)) return

    if (Math.abs((audio.currentTime || 0) - audioState.currentTime) <= 2) return

    try {
      audio.currentTime = audioState.currentTime
    } catch {
      // Ignore seek races while metadata is still loading.
    }
  }, [audioState.currentTime, audioState.topic?.audio_url])

  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    if (!audioState.topic) {
      navigator.mediaSession.metadata = null
      navigator.mediaSession.playbackState = 'none'
      return
    }

    const artworkUrl = audioState.topic.artwork_url || audioState.topic.image_url
    navigator.mediaSession.metadata = new MediaMetadata({
      title: audioState.topic.title,
      artist: 'InsightAtlas Podcast',
      album: audioState.topic.category || 'Intelligence Report',
      artwork: buildMediaArtwork(artworkUrl),
    })
    navigator.mediaSession.playbackState = audioState.isPlaying ? 'playing' : 'paused'

    navigator.mediaSession.setActionHandler('play', () => {
      void playAudio()
    })
    navigator.mediaSession.setActionHandler('pause', () => {
      pauseAudio()
    })
    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      handleSkip(-(details.seekOffset || 30))
    })
    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      handleSkip(details.seekOffset || 30)
    })
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number') {
        handleSeek(details.seekTime)
      }
    })

    return () => {
      navigator.mediaSession.setActionHandler('play', null)
      navigator.mediaSession.setActionHandler('pause', null)
      navigator.mediaSession.setActionHandler('seekbackward', null)
      navigator.mediaSession.setActionHandler('seekforward', null)
      navigator.mediaSession.setActionHandler('seekto', null)
    }
  }, [audioState.isPlaying, audioState.topic, handleSeek, handleSkip, pauseAudio, playAudio])

  useEffect(() => () => {
    cleanupBlobUrl()
  }, [cleanupBlobUrl])

  if (!audioState.topic) return null

  const progressPercent =
    audioState.duration > 0 ? Math.min(100, (audioState.currentTime / audioState.duration) * 100) : 0
  const artworkUrl = audioState.topic.artwork_url || audioState.topic.image_url
  const isPending = audioState.status === 'processing' || audioState.status === 'queued'

  return (
    <div
      data-testid="audio-player-shell"
      className={`fixed bottom-[calc(env(safe-area-inset-bottom)+6.75rem)] md:bottom-6 z-50 transition-all duration-300 ease-in-out ${
        isDocked
          ? 'left-3 right-3 md:left-auto md:right-6'
          : 'left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2'
      }`}
      onMouseEnter={() => {
        if (!isDocked) {
          setIsExpanded(true)
        }
      }}
      onMouseLeave={() => {
        if (!isDocked) {
          setIsExpanded(false)
        }
      }}
    >
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" />

      <div
        data-testid="audio-player-panel"
        className={`backdrop-blur-xl bg-white/90 dark:bg-gray-900/90 border border-gray-200/50 dark:border-gray-700/50 shadow-2xl overflow-hidden transition-all duration-300 ${
          isDocked
            ? 'w-full md:w-[15.5rem] rounded-[1.75rem] md:rounded-full'
            : isExpanded
              ? 'w-full md:w-[28rem] rounded-2xl'
              : 'w-full md:w-80 rounded-2xl'
        }`}
      >
        <div
          className={
            isDocked
              ? 'p-2.5'
              : 'max-h-[calc(100dvh-10rem)] overflow-y-auto overscroll-contain p-4 md:max-h-[calc(100dvh-4rem)]'
          }
        >
          <div className={`flex justify-between gap-3 ${isDocked ? 'items-center mb-0' : 'items-start mb-3'}`}>
            <button
              type="button"
              className="flex items-center gap-3 min-w-0 flex-1 text-left"
              onClick={() => {
                if (isDocked) {
                  setIsDocked(false)
                }
              }}
              disabled={!isDocked}
            >
              {artworkUrl ? (
                <img
                  src={toAbsoluteUrl(artworkUrl)}
                  alt={audioState.topic.title}
                  className={isDocked
                    ? 'w-9 h-9 rounded-xl object-cover border border-gray-200 dark:border-gray-700'
                    : 'w-12 h-12 rounded-xl object-cover border border-gray-200 dark:border-gray-700'}
                />
              ) : (
                <div className={`${isDocked ? 'w-9 h-9' : 'w-12 h-12'} rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shadow-sm`}>
                  <svg className={isDocked ? 'w-4 h-4' : 'w-6 h-6'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 18V6l10-2v12M9 18a3 3 0 11-6 0 3 3 0 016 0zm10-2a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              )}

              <div className="min-w-0 flex-1">
                <h4 className={`${isDocked ? 'text-xs' : 'text-sm'} font-semibold text-gray-900 dark:text-white truncate`}>
                  {audioState.topic.title}
                </h4>
                <p className={`${isDocked ? 'text-[11px]' : 'text-xs'} text-gray-600 dark:text-gray-400 truncate`}>
                  {isPending
                    ? 'Preparing podcast...'
                    : isDocked
                      ? `${formatTime(audioState.currentTime)} / ${formatTime(audioState.duration)}`
                      : 'AI-Generated Podcast'}
                </p>
              </div>
            </button>

            <div className={`flex items-center ${isDocked ? 'gap-1.5' : 'gap-2'}`}>
              {isDocked ? (
                <>
                  <button
                    onClick={() => void handlePlayPause()}
                    disabled={isPending}
                    className={`shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-white transition-all duration-200 shadow-lg ${
                      isPending
                        ? 'bg-violet-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 hover:scale-105'
                    }`}
                    title={audioState.isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPending ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 018.66 5H17.8A7 7 0 0012 5V2z" />
                      </svg>
                    ) : audioState.isPlaying ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                    <span className="sr-only">{audioState.isPlaying ? 'Pause' : 'Play'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDocked(false)}
                    className="shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Expand audio player"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 14l6-6 6 6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Close audio player"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsDocked(true)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Minimize audio player"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                    </svg>
                  </button>
                  <button
                    onClick={handleClose}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Close audio player"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {!isDocked && (
            <div className="mb-3">
            <div className="relative">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <input
                type="range"
                min="0"
                max={Math.max(audioState.duration, 0)}
                value={Math.min(audioState.currentTime, audioState.duration || audioState.currentTime)}
                onChange={(event) => handleSeek(Number(event.target.value))}
                className="absolute inset-0 w-full h-1.5 opacity-0 cursor-pointer"
                aria-label="Seek podcast"
              />
            </div>

            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{formatTime(audioState.currentTime)}</span>
              <span>{formatTime(audioState.duration)}</span>
            </div>
            </div>
          )}

          {!isDocked && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => handleSkip(-30)}
                className="p-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Rewind 30 seconds"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
                <span className="sr-only">Rewind 30 seconds</span>
              </button>

              <button
                onClick={() => void handlePlayPause()}
                disabled={isPending}
                className={`p-3 rounded-full text-white transition-all duration-200 shadow-lg ${
                  isPending
                    ? 'bg-violet-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 hover:scale-105'
                }`}
                title={audioState.isPlaying ? 'Pause' : 'Play'}
              >
                {isPending ? (
                  <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 018.66 5H17.8A7 7 0 0012 5V2z" />
                  </svg>
                ) : audioState.isPlaying ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
                <span className="sr-only">{audioState.isPlaying ? 'Pause' : 'Play'}</span>
              </button>

              <button
                onClick={() => handleSkip(30)}
                className="p-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Skip forward 30 seconds"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span className="sr-only">Skip forward 30 seconds</span>
              </button>
            </div>
          )}

          {!isDocked && isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 gap-3">
              <span className="truncate">Now Playing</span>
              <span className="truncate">{audioState.topic.category?.toUpperCase() || 'INTELLIGENCE'}</span>
            </div>
          )}
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent" />
      </div>
    </div>
  )
}